import { useEffect } from 'react';

import { ADS_CONSENT_EVENTS } from '@/shared/lib/adsConsent';

export const useSyncAdsContext = <Context extends { observed_at: string }>(
  userId: string | undefined,
  getContext: () => Context | undefined,
  saveContext: (context: Context, signal?: AbortSignal) => Promise<void>,
) => {
  useEffect(() => {
    if (!userId) return;
    let inFlight: AbortController | undefined;
    let stopped = false;
    let lastSaved: string | undefined;
    let pending = Promise.resolve();
    // Serialize changes so a delayed grant cannot overwrite a later withdrawal.
    const sync = () => {
      pending = pending.then(async () => {
        if (stopped) return;
        const context = getContext();
        if (!context) return;
        const signature = JSON.stringify({ ...context, observed_at: undefined });
        if (signature === lastSaved) return;
        inFlight = new AbortController();
        const requestController = inFlight;
        const timeout = window.setTimeout(() => requestController.abort(), 2000);
        try {
          await saveContext(context, requestController.signal);
          lastSaved = signature;
        } catch {
          // The server may have saved a request whose response was lost.
          lastSaved = undefined;
          // Retry on the next consent notification or window focus.
        } finally {
          window.clearTimeout(timeout);
          inFlight = undefined;
        }
      });
    };
    sync();
    ADS_CONSENT_EVENTS.forEach((name) => window.addEventListener(name, sync));
    window.addEventListener('focus', sync);
    return () => {
      stopped = true;
      inFlight?.abort();
      ADS_CONSENT_EVENTS.forEach((name) => window.removeEventListener(name, sync));
      window.removeEventListener('focus', sync);
    };
  }, [userId, getContext, saveContext]);
};
